export type Theme = (typeof THEMES)[number]
export type ThemeSetting = (typeof THEME_SETTINGS)[number]

export const DARK_THEME = 'business' as const
export const LIGHT_THEME = 'winter' as const
export const THEMES = [DARK_THEME, LIGHT_THEME] as const

export const DARK_THEME_SETTING = 'dark' as const
export const LIGHT_THEME_SETTING = 'light' as const
export const SYSTEM_THEME_SETTING = 'system' as const
export const THEME_SETTINGS = [DARK_THEME_SETTING, LIGHT_THEME_SETTING, SYSTEM_THEME_SETTING] as const

const STORAGE_KEY = 'user-theme'

const THEME_GETTERS_BY_THEME_SETTING: Record<ThemeSetting, () => Theme> = {
  [SYSTEM_THEME_SETTING]: () => getCurrentSystemTheme(),
  [LIGHT_THEME_SETTING]: () => LIGHT_THEME,
  [DARK_THEME_SETTING]: () => DARK_THEME,
}

function getThemeBySetting(themeSetting: ThemeSetting): Theme {
  const fn = THEME_GETTERS_BY_THEME_SETTING[themeSetting]
  if (!fn) throw new Error(`Invalid themeSetting: ${themeSetting}`)
  return fn()
}

function isThemeSetting(maybeTheme: string): maybeTheme is ThemeSetting {
  return maybeTheme === LIGHT_THEME_SETTING || maybeTheme === DARK_THEME_SETTING || maybeTheme === SYSTEM_THEME_SETTING
}

export function getThemeSettingFromStorage(): ThemeSetting {
  const storedTheme = localStorage.getItem(STORAGE_KEY)
  if (!storedTheme || !isThemeSetting(storedTheme)) {
    return 'system'
  }
  return storedTheme
}

function saveThemeSettingToStorage(themeSetting: ThemeSetting): void {
  if (!isThemeSetting(themeSetting)) {
    throw new Error(`Not a theme setting: ${themeSetting}`)
  }
  localStorage.setItem(STORAGE_KEY, themeSetting)
}

function applyTheme(theme: Theme): void {
  const html = document.querySelector('html')
  if (!html) throw new Error('No html element found')
  html.setAttribute('data-theme', theme)
}

export function applyThemeSetting(themeSetting: ThemeSetting): void {
  saveThemeSettingToStorage(themeSetting)
  const theme = getThemeBySetting(themeSetting)
  applyTheme(theme)
}

function onSystemThemeChange(callback: (theme: Theme) => void): void {
  window.matchMedia('(prefers-color-scheme: dark)').addEventListener('change', e => {
    const isDarkTheme = e.matches
    const theme = isDarkTheme ? DARK_THEME : LIGHT_THEME
    return callback(theme)
  })
}

function getCurrentSystemTheme(): Theme {
  const isDarkTheme = !!window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches
  return isDarkTheme ? DARK_THEME : LIGHT_THEME
}

/**
 * Call only once.
 * Whenever the device's (system) theme will change, we will apply that new theme, assuming that the user has picked 'system' in their theme settings.
 */
export function initializeThemeChangeOnSystemThemeChange(): void {
  onSystemThemeChange(theme => {
    const settingFromStorage = getThemeSettingFromStorage()
    if (settingFromStorage !== SYSTEM_THEME_SETTING) {
      // user has picked explicit theme, ignore system theme
      return
    }
    applyTheme(theme)
  })
}

export function applyConfiguredTheme(): void {
  const settingFromStorage = getThemeSettingFromStorage()
  const theme = getThemeBySetting(settingFromStorage)
  applyTheme(theme)
}
